import { defineEvent, defineFacet, defineModule, f, msg, type Fact, type Formatters, type Issue, type Quantity } from "../../contract";
import { FORMULAS, MISSING_TEXT, MPPA_TEXT, RATIO_TEXT, reducePerformance } from "./shared/calc";
import {
  FILL, INTAKE_BASIS, MARBLING, MATCH_BASIS, QUALITY_GRADES, WEIGH_METHODS, WEIGHT_CONTEXTS,
  type CarcassData, type GroupWeightData, type GroupWeightState, type IntakeData, type MeasureData, type PerformanceState, type UltrasoundData, type WeightData,
} from "./shared/types";

export type * from "./shared/types";

const AREA_UNITS = { sq_in: "sq in", sq_cm: "sq cm" } as const;
const LENGTH = ["in", "cm", "mm"] as const;
const WEIGHT_UNITS = ["lb", "kg"] as const;

// ---------------------------------------------------------------- weight

const weightFields = {
  weight: f.quantity("Weight", "weight", { units: WEIGHT_UNITS, agent: "The number said, with its unit. When no unit is said, use the ranch's weight unit and mark evidence default." }),
  context: f.enum("Weighing", WEIGHT_CONTEXTS, {
    agent: "birth for a newborn's first weight; weaning when calves are weaned or for 'weaning weights'; yearling for yearling weights; arrival when cattle just came in; sale or ship for sale-barn or shipping weights; routine for any other weigh-up. Leave out when unclear.",
  }),
  method: f.enum("How weighed", WEIGH_METHODS, { level: "more", agent: "estimate only when they say they guessed or eyeballed it; tape for a weight tape. Leave out for a normal scale weight unless they say scale." }),
  condition: f.enum("Fill", FILL, { level: "more", help: "Full or shrunk at weighing, when it matters for a sale or test." }),
  shrink_pct: f.number("Pencil shrink", { level: "more", min: 0, max: 15, suffix: "%", help: "A sale deduction. The weight stays as the scale read it." }),
  reweigh: f.bool("Reweigh", { level: "more", help: "A second reading of the same animal. The later reading is the one used." }),
  group_code: f.text("Management group code", { level: "more", help: "The group code you report to your breed association, if any." }),
  scale_ref: f.text("Scale or session", { level: "more" }),
  reading_id: f.text("Scale reading ID", { level: "more", help: "From a connected scale, so the same reading is never saved twice." }),
  stable: f.bool("Stable reading", { level: "more" }),
  adjusted_weight: f.quantity("Adjusted weight from another source", "weight", { level: "more", units: WEIGHT_UNITS, help: "An adjusted value someone else worked out, like your breed association. Kept as imported, next to Hondo's own." }),
  adjusted_by: f.text("Adjusted by", { level: "more" }),
  ratio: f.number("Ratio from that source", { level: "more", min: 1, max: 300 }),
  source_ref: f.text("Source", { level: "more", help: "File and row it came from" }),
  notes: f.text("Notes", { level: "more" }),
};

function contextPhrase(d: WeightData, x: Formatters, animal: string): string {
  const weight = x.quantity(d.weight);
  switch (d.context) {
    case "birth": return x.t("{{animal}} birth weight {{weight}}", { animal, weight });
    case "weaning": return x.t("{{animal}} weaning weight {{weight}}", { animal, weight });
    case "yearling": return x.t("{{animal}} yearling weight {{weight}}", { animal, weight });
    case "arrival": return x.t("{{animal}} arrival weight {{weight}}", { animal, weight });
    case "sale": return x.t("{{animal}} sale weight {{weight}}", { animal, weight });
    case "ship": return x.t("{{animal}} shipping weight {{weight}}", { animal, weight });
    default: return x.t("{{animal}} weighed {{weight}}", { animal, weight });
  }
}

const weight = defineEvent<WeightData>({
  type: "performance.weight",
  label: "Weight",
  description: "One animal's weight, with what kind of weighing it was and how it was taken.",
  icon: "scale",
  tone: "perf",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: weightFields,
  summary: (e, x) => {
    const animal = e.subjects.find((s) => s.role === "animal")?.label ?? "";
    const d = e.data;
    const parts: string[] = [];
    if (d.weight) parts.push(contextPhrase(d, x, animal));
    else if (d.adjusted_weight) parts.push(x.t("{{animal}} adjusted weight {{weight}} from {{source}}", { animal, weight: x.quantity(d.adjusted_weight), source: d.adjusted_by ?? x.t("another source") }));
    if (d.method === "estimate") parts.push(x.t("estimated"));
    if (d.method === "tape") parts.push(x.t("weight tape"));
    if (d.reweigh) parts.push(x.t("reweigh"));
    if (d.condition === "shrunk") parts.push(x.t("shrunk"));
    return parts.join(", ");
  },
  agent: {
    when: "One animal was weighed (scale, weight tape or eyeballed). One event per animal with that animal's own weight. Birth weights of newborn calves, weaning, yearling, arrival, sale and routine weights.",
    avoid: "Never copy one weight onto several animals: when only an average or a total for a group was given, use performance.group_weight. Body condition and other scores are performance.measurement. If a calving record is being proposed and has a birth weight field, put the calf's birth weight there instead.",
    examples: ["42P weighed 78 at birth", "weighed the heifers: 2501 812, 2502 790", "B12 went 1,980 on the scale", "weaned 47 today, 585 pounds"],
  },
  validate: (d) => {
    const issues: Issue[] = [];
    if (!d.weight && !d.adjusted_weight) issues.push({ path: "weight", message: "Enter the weight." });
    if (d.weight && d.weight.value <= 0) issues.push({ path: "weight", message: "A weight has to be more than zero." });
    if (d.weight && toPounds(d.weight) > 4000) issues.push({ path: "weight", message: "That weight is too heavy for one animal. Check the number." });
    if (d.weight && d.context === "birth" && toPounds(d.weight) > 250) issues.push({ path: "weight", message: "That is too heavy for a birth weight. Check the number." });
    issues.push(...unitIssues({ weight: d.weight, adjusted_weight: d.adjusted_weight }, WEIGHT_UNITS));
    return issues;
  },
});

function toPounds(q: Quantity): number {
  return q.unit === "kg" ? q.value * 2.20462262 : q.value;
}

function unitIssues(fields: Record<string, Quantity | undefined>, allowed: readonly string[]): Issue[] {
  return Object.entries(fields).filter(([, q]) => q && !allowed.includes(q.unit)).map(([path]) => ({ path, message: "Use a unit that fits this measurement." }));
}

// ---------------------------------------------------------------- group average

const groupWeight = defineEvent<GroupWeightData>({
  type: "performance.group_weight",
  label: "Group average weight",
  description: "An average or total weight for a group weighed together. Never copied onto individual animals.",
  icon: "scale",
  tone: "perf",
  subjects: [
    { role: "group", kind: "group", label: "Group" },
    { role: "place", kind: "place", label: "Place" },
  ],
  fields: {
    average: f.quantity("Average weight", "weight", { required: true, units: WEIGHT_UNITS, agent: "The average per head. When only a total was said, divide by the head count and mark evidence calculated." }),
    head: f.int("Head", { required: true, min: 1, agent: "How many head were weighed. When they weighed a whole group, use its head count from find_records and mark evidence inferred; otherwise ask." }),
    total: f.quantity("Total weight", "weight", { level: "more", units: WEIGHT_UNITS }),
    context: f.enum("Weighing", WEIGHT_CONTEXTS),
    description: f.text("Which cattle", { help: "When there's no group or place for them, say which cattle these are." }),
    method: f.enum("How weighed", WEIGH_METHODS, { level: "more" }),
    condition: f.enum("Fill", FILL, { level: "more" }),
    shrink_pct: f.number("Pencil shrink", { level: "more", min: 0, max: 15, suffix: "%" }),
    notes: f.text("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const who = e.subjects.find((s) => s.role === "group")?.label ?? e.data.description ?? e.subjects.find((s) => s.role === "place")?.label ?? "";
    const base = x.t("{{who}} averaged {{weight}}, {{head}}", { who, weight: x.quantity(e.data.average), head: x.count(e.data.head, "{{count}} head", "{{count}} head") });
    return e.data.context && e.data.context !== "routine" ? `${base} (${x.t(WEIGHT_CONTEXTS[e.data.context]).toLowerCase()})` : base;
  },
  agent: {
    when: "A group was weighed together, or only an average or total was given: 'weaned the calves, average 550', 'the load weighed 38,400, 62 head'. Needs a head count.",
    avoid: "Not when individual weights were said; then one performance.weight per animal. This never sets any animal's own weight.",
    examples: ["weaned the calves, average 550", "the steers in pen 2 averaged 845, 40 head"],
  },
  validate: (d, subjects) => {
    const issues: Issue[] = [];
    if (!subjects.some((s) => s.role === "group" || s.role === "place") && !d.description) issues.push({ path: "description", message: "Say which cattle were weighed: a group, a place or a few words." });
    if (d.average && d.average.value <= 0) issues.push({ path: "average", message: "A weight has to be more than zero." });
    issues.push(...unitIssues({ average: d.average, total: d.total }, WEIGHT_UNITS));
    return issues;
  },
});

// ---------------------------------------------------------------- measurements and scores

const BCS_LABELS: Record<number, string> = {
  1: "Emaciated", 2: "Very thin", 3: "Thin", 4: "Borderline", 5: "Moderate", 6: "Good", 7: "Very good", 8: "Fat", 9: "Very fat",
};

export const CODEBOOKS = {
  bcs: BCS_LABELS,
  docility: { 1: "Docile", 2: "Restless", 3: "Nervous", 4: "Flighty", 5: "Aggressive", 6: "Very aggressive" } as Record<number, string>,
  udder: { 1: "Very pendulous, broken floor", 3: "Pendulous", 5: "Intermediate", 7: "Tight", 9: "Very tight" } as Record<number, string>,
  teat: { 1: "Very large, balloon-shaped", 3: "Large", 5: "Intermediate", 7: "Small", 9: "Very small" } as Record<number, string>,
};

const scoreEnum = (labels: Record<number, string>, max: number) => Object.fromEntries(Array.from({ length: max }, (_, i) => [String(i + 1), labels[i + 1] ? `${i + 1} ${labels[i + 1]}` : String(i + 1)])) as Record<string, string>;

const measureFields = {
  bcs: f.number("Body condition score", { min: 1, max: 9, step: 0.5, help: "1 emaciated to 9 very fat (beef 1–9 scale).", agent: "Beef 1 to 9 scale, half scores allowed. Only when a score was said; never infer one from 'thin' or 'looks good'." }),
  scrotal: f.quantity("Scrotal circumference", "length", { units: ["cm", "in"], agent: "Centimeters unless they say inches." }),
  hip_height: f.quantity("Hip height", "height", { units: ["in", "cm"], agent: "Measured over the hooks. Inches unless they say centimeters." }),
  docility: f.enum("Docility score", scoreEnum(CODEBOOKS.docility, 6), { help: "BIF chute score, 1 docile to 6 very aggressive." }),
  frame: f.number("Frame score", { level: "more", min: 0, max: 12, step: 0.1, help: "A frame score someone gave or reported. Hondo also works one out from hip height." }),
  udder: f.enum("Udder suspension", scoreEnum(CODEBOOKS.udder, 9), { level: "more", help: "BIF 1–9: 9 very tight, 1 very pendulous." }),
  teat: f.enum("Teat size", scoreEnum(CODEBOOKS.teat, 9), { level: "more", help: "BIF 1–9: 9 very small, 1 very large." }),
  foot_angle: f.int("Foot angle", { level: "more", min: 1, max: 9, help: "1–9 foot score; 5 is ideal, further from 5 is further from ideal." }),
  claw_set: f.int("Claw set", { level: "more", min: 1, max: 9, help: "1–9 foot score; 5 is ideal, further from 5 is further from ideal." }),
  muscle: f.int("Muscle score", { level: "more", min: 1, max: 9, help: "1 light muscled to 9 heavy muscled." }),
  pelvic_area: f.number("Pelvic area", { level: "more", min: 50, max: 400, suffix: "cm²", help: "Height times width, in square centimeters." }),
  measured_by: f.text("Scored or measured by", { level: "more" }),
  notes: f.text("Notes", { level: "more" }),
};

const measurement = defineEvent<MeasureData>({
  type: "performance.measurement",
  label: "Scores and measurements",
  description: "Body condition, scrotal circumference, hip height, frame, docility, udder, feet, muscle and pelvic area.",
  icon: "ruler",
  tone: "perf",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", required: true, many: true, help: "Everyone listed got these same scores." }],
  fields: measureFields,
  summary: (e, x) => {
    const d = e.data;
    const parts: string[] = [];
    if (d.bcs !== undefined) parts.push(x.t("BCS {{score}}", { score: d.bcs }));
    if (d.scrotal) parts.push(x.t("scrotal {{value}}", { value: x.quantity(d.scrotal) }));
    if (d.hip_height) parts.push(x.t("hip height {{value}}", { value: x.quantity(d.hip_height) }));
    if (d.frame !== undefined) parts.push(x.t("frame {{score}}", { score: d.frame }));
    if (d.docility) parts.push(x.t("docility {{score}}", { score: d.docility }));
    if (d.udder) parts.push(x.t("udder {{score}}", { score: d.udder }));
    if (d.teat) parts.push(x.t("teats {{score}}", { score: d.teat }));
    if (d.foot_angle !== undefined) parts.push(x.t("foot angle {{score}}", { score: d.foot_angle }));
    if (d.claw_set !== undefined) parts.push(x.t("claw set {{score}}", { score: d.claw_set }));
    if (d.muscle !== undefined) parts.push(x.t("muscle {{score}}", { score: d.muscle }));
    if (d.pelvic_area !== undefined) parts.push(x.t("pelvic area {{value}} cm²", { value: d.pelvic_area }));
    const animals = e.subjects.filter((s) => s.role === "animal");
    const who = animals.length === 1 ? animals[0].label ?? "" : x.count(animals.length, "{{count}} animal", "{{count}} animals");
    return who ? `${who}: ${x.list(parts)}` : x.list(parts);
  },
  agent: {
    when: "Scores or measurements: body condition (BCS), scrotal circumference, hip height, frame, docility, udder and teats, feet, muscle, pelvic area. Several animals with the same score go on one event; an animal with a different score gets its own event. 'Mostly 5s, 23 is a 4' means everyone else in that group scored 5.",
    avoid: "Not weights (performance.weight). Not a breeding soundness exam (health). Don't turn words like 'thin' or 'good feet' into a number; leave a note instead.",
    examples: ["B31 scrotal 38", "BCS on the fall cows mostly 5s, 23 is a 4", "2503 docility 2, hip height 48 inches"],
  },
  validate: (d) => {
    const keys = ["bcs", "scrotal", "hip_height", "frame", "docility", "udder", "teat", "foot_angle", "claw_set", "muscle", "pelvic_area"] as const;
    const issues: Issue[] = keys.some((k) => d[k] !== undefined && d[k] !== null) ? [] : [{ path: "bcs", message: "Enter at least one score or measurement." }];
    if (d.bcs !== undefined && (d.bcs * 2) % 1 !== 0) issues.push({ path: "bcs", message: "Body condition is scored in whole or half points." });
    issues.push(...unitIssues({ scrotal: d.scrotal }, ["cm", "in"]), ...unitIssues({ hip_height: d.hip_height }, ["in", "cm"]));
    return issues;
  },
});

// ---------------------------------------------------------------- ultrasound

const ultrasound = defineEvent<UltrasoundData>({
  type: "performance.ultrasound",
  label: "Ultrasound scan",
  description: "Carcass ultrasound for one live animal: marbling (IMF), ribeye area and fat.",
  icon: "activity",
  tone: "perf",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    imf: f.number("Intramuscular fat (IMF)", { min: 0, max: 20, step: 0.01, suffix: "%" }),
    rea: f.number("Ribeye area", { min: 1, max: 200, step: 0.01 }),
    rea_unit: f.enum("Ribeye area in", AREA_UNITS, { agent: "sq_in unless they say square centimeters" }),
    rib_fat: f.quantity("Rib fat", "length", { units: LENGTH, agent: "Inches unless they say cm or mm" }),
    rump_fat: f.quantity("Rump fat", "length", { level: "more", units: LENGTH }),
    scan_weight: f.quantity("Scan weight", "weight", { level: "more", units: WEIGHT_UNITS }),
    technician: f.text("Technician", { level: "more" }),
    lab: f.text("Lab", { level: "more" }),
    sample_id: f.text("Sample or barn sheet ID", { level: "more" }),
    imf_ratio: f.number("IMF ratio from the lab", { level: "more", min: 1, max: 300 }),
    rea_ratio: f.number("Ribeye ratio from the lab", { level: "more", min: 1, max: 300 }),
    fat_ratio: f.number("Fat ratio from the lab", { level: "more", min: 1, max: 300 }),
    group_code: f.text("Management group code", { level: "more" }),
    source_ref: f.text("Source", { level: "more", help: "File and row it came from" }),
    notes: f.text("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const animal = e.subjects.find((s) => s.role === "animal")?.label ?? "";
    const parts: string[] = [];
    if (d.imf !== undefined) parts.push(x.t("IMF {{value}}%", { value: d.imf }));
    if (d.rea !== undefined) parts.push(x.t("ribeye {{value}} {{unit}}", { value: d.rea, unit: x.t(AREA_UNITS[d.rea_unit ?? "sq_in"]) }));
    if (d.rib_fat) parts.push(x.t("rib fat {{value}}", { value: x.quantity(d.rib_fat) }));
    if (d.rump_fat) parts.push(x.t("rump fat {{value}}", { value: x.quantity(d.rump_fat) }));
    return `${x.t("{{animal}} scanned", { animal })}: ${x.list(parts)}`;
  },
  agent: {
    when: "Ultrasound scan results for one live animal: IMF (marbling), ribeye area, rib or rump fat.",
    avoid: "Not results from a harvested carcass (performance.carcass). Not pregnancy ultrasound (breeding).",
    examples: ["2503 scanned 4.1 IMF, 11.8 ribeye, .35 fat"],
  },
  validate: (d) => {
    const issues: Issue[] = [];
    if (d.imf === undefined && d.rea === undefined && !d.rib_fat && !d.rump_fat) issues.push({ path: "imf", message: "Enter at least one scan result." });
    if (d.rea !== undefined && !d.rea_unit) issues.push({ path: "rea_unit", message: "Say whether ribeye area is in square inches or square centimeters." });
    issues.push(...unitIssues({ rib_fat: d.rib_fat, rump_fat: d.rump_fat }, LENGTH), ...unitIssues({ scan_weight: d.scan_weight }, WEIGHT_UNITS));
    return issues;
  },
});

// ---------------------------------------------------------------- carcass

const carcass = defineEvent<CarcassData>({
  type: "performance.carcass",
  label: "Carcass results",
  description: "Harvest results for one animal from the packer or plant: carcass weight, grades, ribeye and fat.",
  icon: "award",
  tone: "perf",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    hcw: f.quantity("Hot carcass weight", "weight", { units: WEIGHT_UNITS }),
    quality_grade: f.enum("Quality grade", QUALITY_GRADES),
    yield_grade: f.number("Yield grade", { min: 1, max: 5.9, step: 0.1 }),
    marbling: f.enum("Marbling", MARBLING, { level: "more" }),
    rea: f.number("Ribeye area", { level: "more", min: 1, max: 200, step: 0.01 }),
    rea_unit: f.enum("Ribeye area in", AREA_UNITS, { level: "more" }),
    back_fat: f.quantity("Back fat", "length", { level: "more", units: LENGTH }),
    kph: f.number("Kidney, pelvic and heart fat", { level: "more", min: 0, max: 10, step: 0.1, suffix: "%" }),
    live_weight: f.quantity("Live weight at harvest", "weight", { level: "more", units: WEIGHT_UNITS }),
    dressing_pct: f.number("Dressing", { level: "more", min: 40, max: 80, step: 0.1, suffix: "%" }),
    plant: f.text("Plant", { level: "more" }),
    carcass_id: f.text("Carcass or kill number", { level: "more" }),
    lot: f.text("Packer lot", { level: "more" }),
    grade_as_written: f.text("Grade as written", { level: "more", help: "The grade exactly as the packer wrote it." }),
    match_basis: f.enum("Matched by", MATCH_BASIS, { level: "more" }),
    source_ref: f.text("Source", { level: "more", help: "File and row it came from" }),
    notes: f.text("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const animal = e.subjects.find((s) => s.role === "animal")?.label ?? "";
    const parts: string[] = [];
    if (d.quality_grade) parts.push(x.t(QUALITY_GRADES[d.quality_grade]));
    if (d.yield_grade !== undefined) parts.push(x.t("yield grade {{value}}", { value: d.yield_grade }));
    if (d.hcw) parts.push(x.t("hot carcass {{value}}", { value: x.quantity(d.hcw) }));
    if (d.rea !== undefined) parts.push(x.t("ribeye {{value}} {{unit}}", { value: d.rea, unit: x.t(AREA_UNITS[d.rea_unit ?? "sq_in"]) }));
    return `${x.t("{{animal}} carcass", { animal })}: ${x.list(parts)}`;
  },
  agent: {
    when: "Carcass or harvest results for one animal from the packer or plant: hot carcass weight, quality and yield grade, ribeye, marbling, back fat.",
    avoid: "Not live ultrasound (performance.ultrasound). Never put a lot or pen average on an animal. The sale or settlement money is a sales or finance record.",
    examples: ["the steer 2516 graded Choice, yield 3, hot carcass 842"],
  },
  validate: (d) => {
    const issues: Issue[] = [];
    if (!d.hcw && !d.quality_grade && d.yield_grade === undefined && d.rea === undefined && !d.marbling && !d.back_fat && !d.grade_as_written) {
      issues.push({ path: "hcw", message: "Enter at least one carcass result." });
    }
    if (d.rea !== undefined && !d.rea_unit) issues.push({ path: "rea_unit", message: "Say whether ribeye area is in square inches or square centimeters." });
    issues.push(...unitIssues({ hcw: d.hcw, live_weight: d.live_weight }, WEIGHT_UNITS), ...unitIssues({ back_fat: d.back_fat }, LENGTH));
    return issues;
  },
});

// ---------------------------------------------------------------- feed intake

const intake = defineEvent<IntakeData>({
  type: "performance.intake",
  label: "Feed intake",
  description: "Feed one animal ate over a period, measured or shared out from pen feed, for gain and efficiency tests.",
  icon: "feed",
  tone: "perf",
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    as_fed: f.quantity("Feed eaten, as fed", "weight", { units: WEIGHT_UNITS }),
    dry_matter: f.quantity("Feed eaten, dry matter", "weight", { units: WEIGHT_UNITS }),
    basis: f.enum("How it was measured", INTAKE_BASIS, { required: true, agent: "measured only for an individual intake system; allocated when pen feed was divided among animals." }),
    days: f.int("Days covered", { min: 1, max: 400, help: "Only needed when the dates don't say it." }),
    source: f.text("Intake system or source", { level: "more" }),
    notes: f.text("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const animal = e.subjects.find((s) => s.role === "animal")?.label ?? "";
    const amount = d.dry_matter ? x.t("{{value}} dry matter", { value: x.quantity(d.dry_matter) }) : x.t("{{value}} as fed", { value: x.quantity(d.as_fed) });
    return x.t("{{animal}} ate {{amount}} ({{basis}})", { animal, amount, basis: x.t(INTAKE_BASIS[d.basis]).toLowerCase() });
  },
  agent: {
    when: "Feed one animal ate over a period, from an intake system or divided out from pen feed, during a gain or efficiency test. Use a date_range for the period.",
    avoid: "Not feeding a pen or herd (feeding module), and not a ration.",
    examples: ["bull 1903 ate 1,640 pounds dry matter from Oct 1 to Nov 1 on the intake bunks"],
  },
  validate: (d) => [
    ...(!d.as_fed && !d.dry_matter ? [{ path: "as_fed", message: "Enter how much feed, as fed or dry matter." }] : []),
    ...unitIssues({ as_fed: d.as_fed, dry_matter: d.dry_matter }, WEIGHT_UNITS),
  ],
});

// ---------------------------------------------------------------- facets

const fmtQ = (q: { value: number; unit: string }) => `${Math.round(q.value * 10) / 10} ${q.unit}`;

const performanceFacet = defineFacet<PerformanceState>({
  id: "performance",
  subject: "animal",
  version: 1,
  eventTypes: ["performance.weight", "performance.measurement", "performance.ultrasound", "performance.carcass", "breeding.calving"],
  reduce: (events) => reducePerformance(events),
  badges: (s, { today }) => {
    if (!s.bcs || s.bcs.score > 4) return [];
    const age = Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${s.bcs.on}T12:00:00Z`)) / 86_400_000);
    return age <= 120 ? [{ label: msg("BCS {{score}}", { score: s.bcs.score }), tone: "warn", icon: "ruler" }] : [];
  },
  facts: (s) => {
    const out: Fact[] = [];
    if (s.last_weight) {
      const values = { weight: fmtQ(s.last_weight), date: s.last_weight.on };
      out.push({ label: "Last weight", value: s.last_weight.estimate ? msg("{{weight}} estimated, {{date}}", values) : msg("{{weight}}, {{date}}", values), icon: "scale", tone: "perf" });
    }
    const gain = s.adg ?? s.adg_birth;
    if (gain) out.push({ label: s.adg ? "Gain" : "Gain since birth", value: msg("{{value}} {{unit}} since {{date}}", { value: gain.value, unit: gain.unit === "kg_per_day" ? "kg/day" : "lb/day", date: gain.from }), icon: "activity" });
    const wean = s.weights.weaning;
    if (wean?.adjusted) out.push({ label: "Adjusted weaning weight", value: msg("{{value}} lb, calculated", { value: Math.round(wean.adjusted.value) }), icon: "scale" });
    else if (wean) out.push({ label: "Weaning weight", value: msg("{{weight}}, {{date}}", { weight: fmtQ(wean), date: wean.on }), icon: "scale" });
    if (s.bcs) out.push({ label: "Body condition", value: msg("{{score}}, {{date}}", { score: s.bcs.score, date: s.bcs.on }), icon: "ruler", tone: s.bcs.score <= 4 ? "warn" : undefined });
    return out;
  },
  filters: {
    "bcs.score": { label: "Body condition score", type: "number" },
    "weights.weaning.method": { label: "How the weaning weight was taken", type: "enum", options: WEIGH_METHODS },
  },
});

const groupFacet = defineFacet<GroupWeightState>({
  id: "performance",
  subject: "group",
  version: 1,
  eventTypes: ["performance.group_weight"],
  reduce: (events) => {
    const rows = events.filter((e) => e.occurred_at).map((e) => {
      const d = e.data as GroupWeightData;
      return { average: d.average, head: d.head, on: e.occurred_at!.slice(0, 10), event_id: e.id, ...(d.context ? { context: d.context } : {}), ...(d.total ? { total: d.total } : {}) };
    });
    if (!rows.length) return null;
    const last = rows.at(-1)!;
    return { last, history: rows.slice(-24).map(({ total: _total, ...r }) => r) };
  },
  facts: (s) => [{ label: "Group average weight", value: msg("{{weight}}, {{head}} head, {{date}}", { weight: fmtQ(s.last.average), head: s.last.head, date: s.last.on }), icon: "scale" }],
});

// ---------------------------------------------------------------- module

export default defineModule({
  id: "performance",
  label: "Performance",
  description: "Weights, measurements, scores, adjusted weights, gain tests and carcass data.",
  events: [weight, groupWeight, measurement, ultrasound, carcass, intake],
  facets: [performanceFacet, groupFacet],
  jobs: [
    {
      type: "performance.weigh_round",
      label: "Weighing round",
      description: "Weigh a set of cattle one after another, with each animal's last weight and a check beside it.",
      icon: "scale",
      tone: "perf",
      workspace: "performance",
      subjectKind: "animal",
      itemEvent: "performance.weight",
      fields: {
        context: f.enum("Weighing", WEIGHT_CONTEXTS),
        method: f.enum("How weighed", WEIGH_METHODS, { level: "more" }),
        condition: f.enum("Fill", FILL, { level: "more" }),
      },
      agent: { when: "They want to set up weighing a set of cattle: weaning weights, yearling weights, a weigh-up before shipping." },
    },
    {
      type: "performance.gain_test",
      label: "Gain test",
      description: "Follow each animal's gain between its start and end weights, with feed efficiency when intake is recorded.",
      icon: "activity",
      tone: "perf",
      workspace: "performance",
      subjectKind: "animal",
      itemEvent: "performance.weight",
      fields: {
        starts_on: f.date("Test starts", { required: true }),
        ends_on: f.date("Test ends"),
        purpose: f.text("Purpose"),
        intake: f.enum("Feed intake", { none: "Not recorded", measured: "Measured for each animal", allocated: "Shared out from pen feed" }),
      },
      stages: [{ id: "running", label: "On test" }, { id: "complete", label: "Results published" }],
      agent: { when: "They want to start a gain test or feed efficiency test on a set of cattle." },
    },
  ],
  workspaces: [{
    id: "performance",
    label: "Measure performance",
    description: "Weighing rounds, missing weights, adjusted weights and ratios, gain tests, ultrasound and carcass returns.",
    icon: "scale",
    group: "care",
    keywords: ["weigh", "weights", "scale", "weaning weight", "birth weight", "yearling weight", "adg", "gain", "ratio", "mppa", "bcs", "body condition", "frame", "scrotal", "hip height", "ultrasound", "carcass", "harvest", "grade", "gain test", "feed efficiency", "performance"],
    keywordsEs: ["pesar", "peso", "pesaje", "báscula", "destete", "peso al nacer", "ganancia", "índice", "condición corporal", "escrotal", "altura", "ultrasonido", "canal", "rendimiento", "prueba de ganancia", "eficiencia"],
    presets: ["seedstock"],
  }],
  settings: {
    assumed_birth_weight: f.quantity("Birth weight to assume when none was recorded", "weight", { units: WEIGHT_UNITS, help: "Leave empty to adjust weaning weights only from recorded birth weights. Results that use it say so." }),
    outlier_pct: f.int("Ask to check weights that change more than", { min: 5, max: 50, suffix: "%", help: "Compared with the animal's last weight." }),
  },
  messages: [
    ...Object.values(FORMULAS).flatMap((x) => [x.label, x.text]), RATIO_TEXT, MPPA_TEXT, ...Object.values(MISSING_TEXT),
    ...Object.values(CODEBOOKS.bcs), ...Object.values(CODEBOOKS.docility), ...Object.values(CODEBOOKS.udder), ...Object.values(CODEBOOKS.teat),
    "Last weight", "Gain", "Gain since birth", "Adjusted weaning weight", "Weaning weight", "Body condition", "Group average weight",
    "That date is before the animal was born.", "That date is after the animal left the herd.", "This scale reading was already saved.",
    "Scrotal circumference is only for bulls.", "Udder and teat scores are only for females.", "Say how many days this intake covers.",
  ],
});
